import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useTheme } from '@/context/ThemeContext';
import { motion, AnimatePresence } from 'framer-motion';
import {
    ShoppingBag,
    Layers,
    Palette,
    Box,
    Shapes,
    Maximize2,
    ArrowRight,
    Plus,
    Sun,
    Moon,
    Circle,
    Type,
    Package,
    Clock,
    User,
    Activity,
    LogOut,
    LogIn,
    Edit3,
    Trash2 as TrashIcon
} from 'lucide-react';
import { supabase } from '@/lib/supabase';


const ThemeToggle = () => {
    const { theme, toggleTheme } = useTheme();
    return (
        <button
            onClick={toggleTheme}
            className="p-2 rounded-full bg-white dark:bg-zinc-900 border border-gray-200 dark:border-zinc-800 text-zinc-500 dark:text-zinc-400 hover:text-zinc-900 dark:hover:text-white transition-colors shadow-sm dark:shadow-none"
            title={`Cambiar a modo ${theme === 'dark' ? 'claro' : 'oscuro'}`}
        >
            {theme === 'dark' ? <Sun size={18} /> : <Moon size={18} />}
        </button>
    );
};

const cardVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: (i) => ({
        opacity: 1,
        y: 0,
        transition: { delay: i * 0.1, duration: 0.5 }
    })
};

const StatCard = ({ title, value, icon: Icon, color, index }) => (
    <motion.div
        custom={index}
        initial="hidden"
        animate="visible"
        variants={cardVariants}
        className="bg-white dark:bg-zinc-900/50 backdrop-blur-md border border-gray-200 dark:border-zinc-800 rounded-xl p-4 sm:p-6 relative overflow-hidden group hover:border-gray-300 dark:hover:border-zinc-700 transition-colors shadow-sm dark:shadow-none"
    >
        <div className={`absolute top-0 right-0 p-4 opacity-10 group-hover:opacity-20 transition-opacity ${color}`}>
            <Icon size={80} className="sm:size-[100px]" />
        </div>

        <div className="relative z-10">
            <div className={`p-2 sm:p-3 rounded-lg w-fit mb-3 sm:mb-4 bg-gray-100 dark:bg-zinc-950 border border-gray-200 dark:border-zinc-800 ${color} bg-opacity-10 dark:bg-opacity-10 text-zinc-900 dark:text-white`}>
                <Icon size={20} className={`sm:size-6 ${color.replace('text-', 'text-current ')}`} />
            </div>
            <p className="text-zinc-500 text-[10px] sm:text-sm font-medium uppercase tracking-wider">{title}</p>
            <h3 className="text-2xl sm:text-4xl font-black text-zinc-900 dark:text-white mt-0.5 sm:mt-1">{value}</h3>
        </div>
    </motion.div>
);

const DashboardHome = () => {
    const [stats, setStats] = useState({
        products: 0,
        categories: 0,
        materials: 0,
        colors: 0,
        textures: 0,
        presentations: 0
    });

    const [activities, setActivities] = useState([]);
    const [loadingActivities, setLoadingActivities] = useState(true);

    const [activeIndex, setActiveIndex] = useState(0);

    const quickActions = [
        { name: 'Base de Productos', path: 'base-productos', icon: Package, color: 'text-blue-500', bg: 'bg-blue-500' },
        { name: 'Categorías', path: 'categorias', icon: Layers, color: 'text-purple-500', bg: 'bg-purple-500' },
        { name: 'Colores', path: 'colores', icon: Palette, color: 'text-pink-500', bg: 'bg-pink-500' },
        { name: 'Materiales', path: 'materiales', icon: Box, color: 'text-orange-500', bg: 'bg-orange-500' },
        { name: 'Texturas', path: 'texturas', icon: Shapes, color: 'text-cyan-500', bg: 'bg-cyan-500' },
        { name: 'Presentaciones', path: 'presentaciones', icon: Maximize2, color: 'text-green-500', bg: 'bg-green-500' },
        { name: 'Bases', path: 'bases', icon: Layers, color: 'text-blue-500', bg: 'bg-blue-500' },
        { name: 'Diámetros', path: 'diametros', icon: Circle, color: 'text-red-500', bg: 'bg-red-500' },
        { name: 'Diseñada Para', path: 'disenada_para', icon: Type, color: 'text-amber-500', bg: 'bg-amber-500' },
    ];

    useEffect(() => {
        const timer = setInterval(() => {
            setActiveIndex((prev) => (prev + 1) % quickActions.length);
        }, 4000);
        return () => clearInterval(timer);
    }, [quickActions.length]);

    useEffect(() => {
        const fetchStats = async () => {
            const getCount = async (table) => {
                const { count } = await supabase.from(table).select('*', { count: 'exact', head: true });
                return count || 0;
            };

            const [p, c, m, col, tex, pres] = await Promise.all([
                getCount('producto_detalle'),
                getCount('categoria'),
                getCount('material'),
                getCount('color'),
                getCount('textura'),
                getCount('presentacion')
            ]);

            setStats({
                products: p,
                categories: c,
                materials: m,
                colors: col,
                textures: tex,
                presentations: pres
            });
        };
        fetchStats();
        fetchActivities();
    }, []);

    const fetchActivities = async () => {
        setLoadingActivities(true);
        try {
            const { data, error } = await supabase
                .from('historial_actividad')
                .select('*')
                .order('creado_en', { ascending: false })
                .limit(20);

            if (error) throw error;
            setActivities(data || []);
        } catch (error) {
            console.error('Error fetching activities:', error);
        } finally {
            setLoadingActivities(false);
        }
    };

    const getActionIcon = (action) => {
        switch (action) {
            case 'INSERT': return <Plus size={14} className="text-green-500" />;
            case 'UPDATE': return <Edit3 size={14} className="text-blue-500" />;
            case 'DELETE': return <TrashIcon size={14} className="text-red-500" />;
            case 'LOGIN': return <LogIn size={14} className="text-purple-500" />;
            default: return <Activity size={14} className="text-zinc-400" />;
        }
    };

    const formatTime = (dateStr) => {
        const date = new Date(dateStr);
        return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    };

    return (
        <div className="space-y-8">
            <motion.div
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                className="flex justify-between items-end"
            >
                <div>
                    <h1 className="text-3xl font-bold text-zinc-900 dark:text-white tracking-tight">
                        Hola, <span className="text-red-600">Emilio</span>
                    </h1>
                    <p className="text-zinc-500 mt-1">Aquí tienes el resumen de tu producción hoy.</p>
                </div>
                <div className="flex items-center gap-3">
                    <ThemeToggle />
                    <div className="bg-white dark:bg-zinc-900 border border-gray-200 dark:border-zinc-800 px-4 py-2 rounded-full text-xs font-mono text-zinc-500 dark:text-zinc-400 flex items-center gap-2 shadow-sm dark:shadow-none">
                        <span className="w-2 h-2 rounded-full bg-green-500 animate-pulse"></span>
                        SISTEMA ONLINE
                    </div>
                </div>
            </motion.div>

            <div className="grid grid-cols-2 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3 sm:gap-6">
                <StatCard index={0} title="Productos" value={stats.products} icon={ShoppingBag} color="text-blue-500" />
                <StatCard index={1} title="Categorías" value={stats.categories} icon={Layers} color="text-purple-500" />
                <StatCard index={2} title="Colores" value={stats.colors} icon={Palette} color="text-pink-500" />
                <StatCard index={3} title="Materiales" value={stats.materials} icon={Box} color="text-orange-500" />
                <StatCard index={4} title="Texturas" value={stats.textures} icon={Shapes} color="text-cyan-500" />
                <StatCard index={5} title="Pres." value={stats.presentations} icon={Maximize2} color="text-green-500" />
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 mt-12">
                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.4 }}
                    className="lg:col-span-2 bg-white dark:bg-zinc-900/30 border border-gray-200 dark:border-zinc-800 rounded-2xl p-6 min-h-[400px] shadow-sm dark:shadow-none flex flex-col"
                >
                    <div className="flex items-center justify-between mb-6">
                        <div className="flex items-center gap-2">
                            <Clock size={18} className="text-red-600" />
                            <h3 className="text-lg font-bold text-zinc-900 dark:text-white">Actividad Reciente</h3>
                        </div>
                        <span className="text-[10px] font-mono bg-gray-100 dark:bg-zinc-800 px-2 py-1 rounded text-zinc-500">Últimos 20 registros</span>
                    </div>

                    <div className="flex-1 overflow-y-auto pr-2 custom-scrollbar">
                        {loadingActivities ? (
                            <div className="h-full flex items-center justify-center text-zinc-500 italic animate-pulse">
                                Cargando actividad...
                            </div>
                        ) : activities.length === 0 ? (
                            <div className="h-full flex flex-col items-center justify-center text-zinc-500 dark:text-zinc-600 text-sm gap-2">
                                <Activity size={32} strokeWidth={1} className="opacity-20" />
                                <span>No hay actividad reciente registrada.</span>
                            </div>
                        ) : (
                            <div className="space-y-3">
                                {activities.map((activity, idx) => (
                                    <motion.div
                                        initial={{ opacity: 0, x: -10 }}
                                        animate={{ opacity: 1, x: 0 }}
                                        transition={{ delay: 0.1 * idx }}
                                        key={activity.id}
                                        className="flex items-start gap-4 p-3 rounded-xl hover:bg-gray-50 dark:hover:bg-zinc-800/50 transition-colors border border-transparent hover:border-gray-100 dark:hover:border-zinc-800/80 group"
                                    >
                                        <div className="mt-1 p-2 rounded-lg bg-gray-100 dark:bg-zinc-900 border border-gray-200 dark:border-zinc-800 transition-colors group-hover:bg-white dark:group-hover:bg-black">
                                            {getActionIcon(activity.accion)}
                                        </div>
                                        <div className="flex-1 min-w-0">
                                            <div className="flex justify-between items-start mb-0.5">
                                                <p className="text-sm font-bold text-zinc-900 dark:text-zinc-100 truncate">
                                                    {activity.detalles}
                                                </p>
                                                <span className="text-[10px] font-mono text-zinc-400 whitespace-nowrap ml-2">
                                                    {formatTime(activity.creado_en)}
                                                </span>
                                            </div>
                                            <div className="flex items-center gap-2">
                                                <span className="text-[10px] uppercase tracking-wider font-black text-red-500 flex items-center gap-1">
                                                    <User size={10} /> {activity.usuario}
                                                </span>
                                                {activity.entidad && (
                                                    <>
                                                        <span className="text-zinc-300 dark:text-zinc-700">•</span>
                                                        <span className="text-[10px] text-zinc-500 font-medium">En {activity.entidad}</span>
                                                    </>
                                                )}
                                            </div>
                                        </div>
                                    </motion.div>
                                ))}
                            </div>
                        )}
                    </div>
                </motion.div>

                <div className="relative bg-white dark:bg-zinc-900/50 border border-gray-200 dark:border-zinc-800 rounded-2xl p-1 overflow-hidden shadow-sm dark:shadow-none">
                    <div className="absolute inset-0 bg-gradient-to-b from-transparent to-gray-100/50 dark:to-black/50 pointer-events-none"></div>

                    <div className="h-full flex flex-col p-6 relative z-10">
                        <h3 className="text-lg font-bold text-zinc-900 dark:text-white mb-1">Acciones Rápidas</h3>
                        <p className="text-zinc-500 text-sm mb-6">Gestiona tu catálogo dinámicamente.</p>

                        <div className="flex-1 flex flex-col justify-center">
                            <div className="relative h-48 w-full">
                                <AnimatePresence mode="wait">
                                    <motion.div
                                        key={activeIndex}
                                        initial={{ opacity: 0, x: 50 }}
                                        animate={{ opacity: 1, x: 0 }}
                                        exit={{ opacity: 0, x: -50 }}
                                        transition={{ duration: 0.4, ease: "easeInOut" }}
                                        className="absolute inset-0 flex flex-col gap-3"
                                    >
                                        <div className={`w-12 h-12 rounded-xl ${quickActions[activeIndex].bg} bg-opacity-20 flex items-center justify-center mb-2 mx-auto`}>
                                            {React.createElement(quickActions[activeIndex].icon, { className: quickActions[activeIndex].color, size: 24 })}
                                        </div>

                                        <div className="text-center mb-4">
                                            <h4 className="text-zinc-900 dark:text-white font-bold text-xl">{quickActions[activeIndex].name}</h4>
                                            <p className="text-zinc-500 text-xs">Administrar catálogo</p>
                                        </div>

                                        <Link
                                            to={quickActions[activeIndex].path}
                                            className="w-full py-3 bg-gray-100 hover:bg-gray-200 dark:bg-zinc-800 dark:hover:bg-zinc-700 text-zinc-900 dark:text-white rounded-lg font-medium text-sm transition-colors border border-gray-200 dark:border-zinc-700 flex items-center justify-center gap-2"
                                        >
                                            Gestionar {quickActions[activeIndex].name} <ArrowRight size={16} />
                                        </Link>

                                        <Link
                                            to="produtos"
                                            className="w-full py-3 bg-red-600 hover:bg-red-500 text-white rounded-lg font-bold text-sm transition-colors shadow-lg shadow-red-900/20 flex items-center justify-center gap-2"
                                        >
                                            <Plus size={16} /> Nuevo Producto
                                        </Link>
                                    </motion.div>
                                </AnimatePresence>
                            </div>
                        </div>

                        <div className="flex justify-center gap-2 mt-4">
                            {quickActions.map((_, idx) => (
                                <div
                                    key={idx}
                                    className={`w-2 h-2 rounded-full transition-all duration-300 ${idx === activeIndex ? 'bg-red-600 w-4' : 'bg-gray-300 dark:bg-zinc-800'}`}
                                />
                            ))}
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default DashboardHome;
